/**
 * The LaTeX view's Preamble tab (DESIGN-14, Ḥassān, 17 Oct): the lines before \begin{document},
 * grouped by what they are for, in the order Lilia writes them. Each line says in plain words what
 * it does, its LaTeX beneath. Lilia's long definitions fold; the author's own lines come last, tagged
 * "You", with the way to edit them.
 */
import i18n from "i18next";
import { useState } from "react";
import { IconChevronRight } from "@tabler/icons-react";
import { testIds } from "@/testIds";
import { cn } from "@/lib/cn";
import { THEOREMS_TITLE, type PreambleRow, type PreambleSummary } from "./latexView";
import styles from "./LatexViewTabs.module.scss";

const T = testIds.latexView;
const FOLD_ROW_OVER = 4;

/** The plain-words line for a row, or null when Lilia has none for it. */
function gloss(row: PreambleRow): string | null {
  const t = row.code.trim();
  const dc = t.match(/^\\documentclass\s*(?:\[([^\]]*)\])?\s*\{([^}]*)\}/);
  if (dc) {
    const opts = (dc[1] ?? "").split(",").map((s) => s.trim());
    const size = opts.find((o) => /^\d+pt$/.test(o));
    const paper = opts.find((o) => /paper$/.test(o))?.replace(/paper$/, "").toUpperCase();
    return i18n.t("editor:latexView.preamble.documentClass", {
      cls: dc[2].charAt(0).toUpperCase() + dc[2].slice(1),
      size: size ? size.replace("pt", " pt") : "",
      paper: paper ?? "",
    }).replace(/\s*,\s*$/, "").replace(/ on $/, "");
  }
  const info = t.match(/^\\(title|author|date)\b/);
  if (info) return i18n.t(`editor:latexView.preamble.info.${info[1]}`);
  for (const p of row.packages ?? []) {
    const k = `editor:latexView.pkg.${p}`;
    if (i18n.exists(k)) return i18n.t(k);
  }
  const setting = t.match(/^\\(graphicspath|lstset|lstdefinelanguage|hypersetup|PassOptionsToPackage|widowpenalty|clubpenalty|normalem)\b/);
  if (setting) {
    const k = `editor:latexView.preamble.setting.${setting[1]}`;
    if (i18n.exists(k)) return i18n.t(k);
  }
  return null;
}

/** A glossed row's code without its end-of-line comments: the plain-words line already says it. */
function withoutComments(code: string): string {
  return code.replace(/(^|[^\\])\s*%.*$/gm, "$1");
}

function Row({ row }: { row: PreambleRow }) {
  const long = row.lines > FOLD_ROW_OVER || !!row.title;
  const [open, setOpen] = useState(false);
  const title = row.title === THEOREMS_TITLE ? i18n.t("editor:latexView.preamble.theorems") : row.title;
  const g = title ?? gloss(row);
  return (
    <div data-testid={T.preambleRow} data-yours={row.yours || undefined} className={styles.pRow}>
      {(g || row.yours) && (
        <div className={styles.pGloss}>
          {g}
          {row.yours && <span className={styles.youTag}>{i18n.t("editor:latexView.preamble.you")}</span>}
        </div>
      )}
      {(!long || open) && <pre className={styles.pCode}>{g ? withoutComments(row.code) : row.code}</pre>}
      {long && (
        <button type="button" data-testid={T.preambleFold} className={styles.fold} onClick={() => setOpen((o) => !o)}>
          {open ? i18n.t("editor:latexView.blocks.showLess") : i18n.t("editor:latexView.preamble.showLines", { count: row.lines })}
        </button>
      )}
    </div>
  );
}

/** The packages a group loads, for a folded group's one-line peek on a phone. */
function peek(rows: PreambleRow[]): string {
  const names = rows.flatMap((r) => r.packages ?? (r.title === THEOREMS_TITLE ? [i18n.t("editor:latexView.preamble.theorems")] : r.title ? [r.title] : []));
  return [...new Set(names)].join(", ");
}

function PhoneGroup({ group, initiallyOpen }: { group: PreambleSummary["groups"][number]; initiallyOpen: boolean }) {
  const [open, setOpen] = useState(initiallyOpen);
  const lines = group.rows.reduce((n, r) => n + r.lines, 0);
  return (
    <section data-testid={T.preambleGroup} data-group={group.key} data-open={open || undefined} className={cn(styles.pGroup, styles.pPhoneGroup)}>
      <button type="button" data-testid={T.preambleGroupToggle} aria-expanded={open} className={styles.pGroupRow} onClick={() => setOpen((o) => !o)}>
        <span className={styles.pGroupRowText}>
          <span className={styles.pGroupTitle}>{i18n.t(`editor:latexView.preamble.group.${group.key}`)}</span>
          {!open && <span className={styles.pPeek}>{peek(group.rows)}</span>}
        </span>
        <span className={styles.pGroupCount}>{open ? i18n.t("editor:latexView.preamble.lines", { count: lines }) : lines}</span>
        <IconChevronRight size={16} className={cn(styles.pChevron, open && styles.pChevronOpen)} aria-hidden="true" />
      </button>
      {open && (
        <div className={styles.pRowsInner}>
          {group.rows.map((r, i) => <Row key={i} row={r} />)}
          {group.notes.map((n, i) => (
            <p key={i} className={styles.pNote}><b>{i18n.t("editor:latexView.preamble.note")}</b> {n}</p>
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * Without onEditPreamble (the phone, read-only) there is no edit link. On a phone (Ḥassān, 18 Oct)
 * the counts are three cells, the intro says the author's lines change on a computer, and each
 * group folds to one row: the first group and the author's own lines start open.
 */
export function LatexPreambleTab({ summary, onEditPreamble, variant = "desktop" }: { summary: PreambleSummary; onEditPreamble?: () => void; variant?: "desktop" | "phone" }) {
  if (variant === "phone") {
    return (
      <div data-testid={T.preamble} className={styles.scroll}>
        <div className={styles.pCells}>
          <div><b>{summary.liliaLines}</b><span>{i18n.t("editor:latexView.preamble.cell.lilia")}</span></div>
          <div><b>{summary.packages}</b><span>{i18n.t("editor:latexView.preamble.cell.packages")}</span></div>
          <div><b>{summary.yourLines}</b><span>{i18n.t("editor:latexView.preamble.cell.you")}</span></div>
        </div>
        <p className={styles.pIntro}>{i18n.t("editor:latexView.preamble.introPhone")}</p>
        <div className={styles.pPhoneGroups}>
          {summary.groups.map((g, i) => <PhoneGroup key={g.key} group={g} initiallyOpen={i === 0 || g.key === "yours"} />)}
        </div>
      </div>
    );
  }
  return (
    <div data-testid={T.preamble} className={styles.scroll}>
      <div className={styles.pHead}>
        <div className={styles.pCounts}>
          <span><b>{summary.liliaLines}</b> {i18n.t("editor:latexView.preamble.fromLilia", { count: summary.liliaLines })}</span>
          <span><b>{summary.packages}</b> {i18n.t("editor:latexView.preamble.packages", { count: summary.packages })}</span>
          <span><b>{summary.yourLines}</b> {i18n.t("editor:latexView.preamble.fromYou", { count: summary.yourLines })}</span>
        </div>
        {onEditPreamble && (
          <button type="button" data-testid={T.editPreamble} className={styles.editLink} onClick={onEditPreamble}>
            {i18n.t("editor:latexView.preamble.edit")}
          </button>
        )}
      </div>
      <p className={styles.pIntro}>{i18n.t("editor:latexView.preamble.intro")}</p>
      {summary.groups.map((g) => (
        <section key={g.key} data-testid={T.preambleGroup} data-group={g.key} className={cn(styles.pGroup, g.key === "yours" && styles.pYours)}>
          <div className={styles.pGroupHead}>
            <span className={styles.pGroupTitle}>{i18n.t(`editor:latexView.preamble.group.${g.key}`)}</span>
            <span className={styles.pGroupCount}>{i18n.t("editor:latexView.preamble.lines", { count: g.rows.reduce((n, r) => n + r.lines, 0) })}</span>
          </div>
          <div className={styles.pRows}>
            {g.rows.map((r, i) => <Row key={i} row={r} />)}
          </div>
          {g.notes.map((n, i) => (
            <p key={i} className={styles.pNote}><b>{i18n.t("editor:latexView.preamble.note")}</b> {n}</p>
          ))}
        </section>
      ))}
    </div>
  );
}
